import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAngularContext } from 'AngularContext';
import { type AnyObject } from '@Types';
import { type StreamForStreamDashboard } from 'Lessons';
import { toStreamForAngular } from '../toStreamForDashboard';

type StreamClassWithCache = { _streamCacheForCurrentUser: Record<string, unknown> };

type CertificateScope = {
    certificateDownloadState: { downloadingCertificate: boolean };
    allowCertificateDownload: (stream: AnyObject) => boolean;
    downloadCertificate: (stream: AnyObject) => void;
    $watch: (expression: string, listener: (value: boolean) => void) => void;
    $destroy: () => void;
};

/*
    Calls the Angular Stream.CertificateHelperMixin through a temporary scope. Other directives share the
    mixin. The cached stream entry is removed first: getCachedOrShow can return a summary stream with no
    locale packs, and Dexie then rejects with an unhandled DataError. No public API removes one cache entry.
*/
export function useCertificateDownload(stream: StreamForStreamDashboard) {
    const $injector = useAngularContext();
    const [isDownloading, setIsDownloading] = useState(false);

    /*
        Build the scope inside the effect. A useMemo keyed on $injector hands back the same scope
        after a remount, but the cleanup below has already destroyed it, and a $watch on a destroyed
        scope never digests -- so the download spinner would never appear again.
    */
    const [scope, setScope] = useState<CertificateScope | null>(null);

    useEffect(() => {
        const $rootScope = $injector.get<{ $new: () => CertificateScope }>('$rootScope');
        const CertificateHelperMixin = $injector.get<{ onLink: (scope: CertificateScope) => void }>(
            'Stream.CertificateHelperMixin',
        );

        const newScope = $rootScope.$new();
        CertificateHelperMixin.onLink(newScope);
        newScope.$watch('certificateDownloadState.downloadingCertificate', downloading =>
            setIsDownloading(!!downloading),
        );
        setScope(newScope);

        return () => {
            setScope(null);
            newScope.$destroy();
        };
    }, [$injector]);

    const streamShim = useMemo(() => toStreamForAngular(stream), [stream]);

    const allowDownload = !!scope && scope.allowCertificateDownload(streamShim);

    const downloadCertificate = useCallback(() => {
        if (!scope) return;

        delete $injector.get<StreamClassWithCache>('Lesson.Stream')._streamCacheForCurrentUser[streamShim.id];

        scope.downloadCertificate(streamShim);
    }, [scope, streamShim, $injector]);

    return { isDownloading, allowDownload, downloadCertificate };
}
